/**
 * System / Light / Dark, in the identity menu.
 *
 * ── WHY A SEGMENTED CONTROL AND NOT A TOGGLE ─────────────────────────
 *
 * A two-state toggle cannot say "follow the OS", and that is the default and
 * the state most admins should stay in. A toggle would force everyone who
 * touched it into a permanent override, so a machine that switches to dark in
 * the evening would stop following — the setting would quietly become worse
 * than never having offered one.
 *
 * ── WHY IT LIVES IN THE IDENTITY MENU ────────────────────────────────
 *
 * It is a preference belonging to the person, not to a screen, and it sits
 * beside the other two things that are about you rather than about the data:
 * Account settings and Sign out. It is also per-browser rather than per-account
 * (localStorage, not the identity service), which the caption says out loud so
 * nobody reports it as a bug when a second machine opens light.
 */
import { C } from '@ridgewill/brand';
import { useState } from 'react';
import { readTheme, setTheme, type ThemeChoice } from '../lib/theme.js';

const OPTIONS: { value: ThemeChoice; label: string }[] = [
  { value: 'system', label: 'System' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

export function ThemeControl() {
  /**
   * Seeded from storage rather than defaulted to `system`.
   *
   * The menu unmounts when it closes, so this component mounts fresh every
   * time it is opened. A `useState('system')` would show System selected to
   * somebody who had chosen Dark — the control lying about the state it
   * controls, on a page that is visibly dark.
   */
  const [choice, setChoice] = useState<ThemeChoice>(readTheme);

  return (
    <div style={{ padding: '.35rem .6rem .5rem' }}>
      <div
        id="rw-theme-label"
        style={{
          fontSize: '.68rem',
          fontWeight: 700,
          letterSpacing: '.06em',
          textTransform: 'uppercase',
          color: C.muted,
          margin: '0 0 .35rem',
        }}
      >
        Theme
      </div>

      <div
        role="radiogroup"
        aria-labelledby="rw-theme-label"
        style={{
          display: 'flex',
          gap: 2,
          padding: 2,
          background: C.page,
          border: `1px solid ${C.border}`,
          borderRadius: 7,
        }}
      >
        {OPTIONS.map(({ value, label }) => {
          const active = value === choice;
          return (
            <button
              key={value}
              type="button"
              /**
               * A radio, not a button, because these three are one choice.
               * `aria-checked` is what tells a screen reader which is in force;
               * the background alone says it only to people who can see it.
               */
              role="radio"
              aria-checked={active}
              onClick={() => {
                setTheme(value);
                setChoice(value);
              }}
              style={{
                flex: 1,
                padding: '.28rem .1rem',
                fontSize: '.75rem',
                fontWeight: active ? 700 : 500,
                // The inverse of the ground, for the same reason the primary
                // button uses it: `C.brand` lightens in dark, so white on it
                // would fall to 2.76:1.
                color: active ? C.surface : C.textMid,
                background: active ? C.brand : 'transparent',
                border: '1px solid transparent',
                borderRadius: 5,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div style={{ fontSize: '.68rem', color: C.muted, margin: '.35rem 0 0', lineHeight: 1.35 }}>
        Saved in this browser only.
      </div>
    </div>
  );
}
